import Decimal, {DecimalSource} from './break_infinity.js';
import {ri} from './runner-idle';
import {
  fancy,
  formatEng,
  sumActivitiesByTag,
  activitiesByTag,
  logisticChance,
  assignIfDefined,
  typedFromEntries,
  all$,
  any$,
  capitalizeFirstLetter,
} from './utils';
import * as D from './decimal_utils';
import {ActivityOption} from './activity-option.js';
import {DynamicCost} from './interfaces/dynamic-value.js';
import {
  ActivityName,
  Gettable,
  ResourceName,
  StatName,
} from './types/runner-idle.js';
import {
  combineLatest,
  identity,
  map,
  shareReplay,
  of,
  share,
  tap,
  filter,
  interval,
  takeWhile,
} from 'rxjs';
import {Activity} from './activity.js';
import * as R from 'ramda';
import {ActivityQueueItem, Configuration} from './activity-queue-item.js';
import {targetPlayerAction} from './creature.js';
import {resolveConfigFile} from 'prettier';
import {landmarkNames} from './initializeLandmarks.js';
const {all} = R;

export const activityNames = [
  'null',
  'rest',
  'meditate',
  // 'walkingMeditation',
  // Travel
  'walk',
  'jog',
  'run',
  'cardio',
  'jump',
  'dash',
  'trainStrength',
  'trainSpeed',
  'trainConstitution',
  'trainEndurance',
  'trainVitality',
  'trainStrSpd',
  'hunt',
  'setDestination',
  'moveTarget',
  'fishing',
  'gather',
  'cook',
  'lightFire',
  'eat',
  'read',
  // 'fight',
] as const;

/**
 * Initialize all the Activity elements.
 *
 * This is where the domain-level data for each Activity is defined.
 * Whenever adding a new Activity, make sure to add it to the `activityNames` array;
 * this is a common source of bugs, embarassingly.
 * I'm actually going to fix this right now, for Resources too aand all of them,
 * if you're reading this check to see if I fixed it,
 * and if I did remove this comment.
 * later:Turns out this is nontrivial,
 * since the names are used to generate the html elements during initialization of ri,
 * but the initializations of the resources/etc reference ri,
 * so the names are needed before we can do the rest of the initializations.
 * Gonna pause and just leave this note, nbd for now.
 *
 * @returns {void}
 */
export function initializeActivities() {
  // Do all the extra stuff we've gotta do that we can do for each
  activityNames.forEach((name) => {
    ri.activities[name].setAttribute('name', name);
    ri.activities[name].classList.add('activity', name);
  });
  // // Any assignments go here
  // function initActivity(name: ActivityName, props: Partial<Activity>) {
  //   Object.assign<Activity, Partial<Activity>>(ri.activities[name], {
  //     name,
  //     displayName: capitalizeFirstLetter(name),
  //     ...props,
  //   });
  // }
  ri.activities.null.setup({
    unlock$: of(false),
    visible$: of(false),
  });

  ri.activities.rest.setup({
    displayName: 'Rest',
    description: 'Rest a while.',
    unlock$: of(true),
    effect$: combineLatest([
      ri.stats.constitution.value$,
      ri.resources.stamina.vacancy$,
    ]).pipe(
      map(
        ([conValue, staminaVacancy]) =>
          new Map([
            [
              'staminaExp',
              {
                value: () => conValue.times(5).min(staminaVacancy),
                string: () => `Stamina gained`,
              },
            ],
            [
              'stamina',
              {
                value: () => conValue.times(5),
                string: () => `${ri.stats.constitution.valueHTML} * 5`,
              },
            ],
            [
              'health',
              {
                value: () => 1,
                string: () => `1`,
              },
            ],
          ])
      )
    ),
  });

  ri.activities.setDestination.setup({
    displayName: 'Set Target Position to Landmark',
    description: 'Move target position to the chosen landmark.',
    // repeatByDefault: false,
    duration$: of(0),
    requiredLandmarks$: of({guidebook: true}),
    unlock$: of(true),
    configuration: {
      __unused: 'EDIT_LANDMARKS',
      landmark: '',
    },
    effect$: of(
      new Map([
        [
          'targetX',
          {
            value: (_, optionIndex: number, configuration: Configuration) =>
              ri.landmarks[
                configuration.landmark as string
              ]?.shape.nearestPointTo(ri.position).x ?? ri.targetPosition.x,
            string: () => '1',
          },
        ],
        [
          'targetY',
          {
            value: (_, optionIndex: number, configuration: Configuration) =>
              ri.landmarks[
                configuration.landmark as string
              ]?.shape.nearestPointTo(ri.position).y ?? ri.targetPosition.y,
            string: () => '1',
          },
        ],
      ])
    ),
  });

  ri.activities.moveTarget.setup({
    displayName: 'Move Target',
    description: 'Move target position.',
    duration$: of(0),
    requiredLandmarks$: of({guidebook: true}),
    requiredStats$: of({perception: 2}),
    configuration: {
      deltaX: new Decimal(0),
      deltaY: new Decimal(0),
      targetX: Decimal.NaN,
      targetY: Decimal.NaN,
    },
    effect$: of(
      new Map([
        [
          'targetX',
          {
            value: (_, __, configuration: Configuration) =>
              configuration.targetX.isNaN()
                ? configuration.deltaX
                : configuration.targetX.minus(ri.targetPosition.x),
            string: () => '1',
          },
        ],
        [
          'targetY',
          {
            value: (_, __, configuration: Configuration) =>
              configuration.targetY.isNaN()
                ? configuration.deltaY
                : configuration.targetY.minus(ri.targetPosition.y),
            string: () => '1',
          },
        ],
      ])
    ),
  });

  // const makeTravelTooltip: (
  //   baseStaminaCost: DecimalSource,
  //   baseDistance: DecimalSource,
  //   duration?: number
  // ) => () => string =
  //   (baseStaminaCost, baseDistance, duration = 1) =>
  //   () => {
  //     if (!ri.settings.showExtraInfo) return '';
  //     const staminaCost = ri.stats.speed.value
  //       .times(baseStaminaCost)
  //       .div(ri.stats.strength.value);Ignoring get or set of property that has [LenientThis] because the “this” object is incorrect.
  //     const distanceTraveled = ri.stats.speed.value.times(baseDistance);
  //     return `
  //       Efficiency: ${distanceTraveled
  //         .div(staminaCost)
  //         .toFixed(2)} meters per stamina
  //       <br> Speed: ${distanceTraveled.div(duration).toFixed(2)} meters per second
  //   `;
  //   };

  const overrideTravel = (
    baseStaminaCost: DecimalSource,
    baseDistance: DecimalSource,
    {
      name = 'stamina' as ActivityName,
      displayName = '',
      description = '',
      duration$ = of(1),
      // duration = 1,
      tags = [] as string[],
      constitutionMultiplier = 0,
      enduranceMultiplier = 0,
      strengthMultiplier = 0,
      speedMultiplier = 0,
      conditionString = '',
      unlock$ = undefined,
      visible$ = undefined,
      requiredStats$ = undefined,
      requiredResources$ = undefined,
    }: Partial<Activity> // TODO fix stat mults into type
  ) => {
    let duration: number | undefined;
    duration$.subscribe((d) => (duration = d));
    if (!duration) {
      duration = 1;
    }
    const logDistance = new Decimal(
      ri.stats.speed.value.times(baseDistance).log10()
    );
    displayName = displayName ?? name;
    const effect = new Map([
      [
        'distance',
        {
          value: (step: number) =>
            step == -1
              ? new Decimal(baseDistance) //ri.stats.speed.value.times(baseDistance)
              : new Decimal(baseDistance).div(duration), //ri.stats.speed.value.times(baseDistance).div(duration),
          string: () => `${baseDistance} * ${ri.stats.speed.valueHTML}`,
        },
      ],
    ]);
    if (constitutionMultiplier != 0) {
      effect.set('constitution', {
        value: (step) =>
          step == -1
            ? logDistance.times(constitutionMultiplier)
            : logDistance.times(constitutionMultiplier).div(duration),
        string: () =>
          `log_10(${fancy(
            'distance',
            formatEng(ri.stats.speed.value.times(baseDistance))
          )} Distance) * ${constitutionMultiplier}`,
      });
    }
    if (enduranceMultiplier != 0) {
      effect.set('endurance', {
        value: (step) =>
          step == -1
            ? logDistance.times(enduranceMultiplier)
            : logDistance.times(enduranceMultiplier).div(duration),
        string: () =>
          `log_10(${fancy(
            'distance',
            formatEng(ri.stats.speed.value.times(baseDistance))
          )} Distance) * ${enduranceMultiplier}`,
      });
    }
    if (strengthMultiplier != 0) {
      effect.set('strength', {
        value: (step) =>
          step == -1
            ? logDistance.times(strengthMultiplier)
            : logDistance.times(strengthMultiplier).div(duration),
        string: () =>
          `log_10(${fancy(
            'distance',
            formatEng(ri.stats.speed.value.times(baseDistance))
          )} Distance) * ${strengthMultiplier}`,
      });
    }
    if (speedMultiplier != 0) {
      effect.set('speed', {
        value: (step) =>
          step == -1
            ? logDistance.times(speedMultiplier)
            : logDistance.times(speedMultiplier).div(duration),
        string: () =>
          `log_10(${fancy(
            'distance',
            formatEng(ri.stats.speed.value.times(baseDistance))
          )} Distance) * ${speedMultiplier}`,
      });
    }
    // Don't add any more, generalize it

    const overrides = {};
    assignIfDefined(overrides, {
      displayName,
      description,
      duration$: combineLatest([duration$, ri.stats.speed.value$]).pipe(
        map(([duration, spd]) => spd.recip().times(duration).toNumber())
      ),
      tags: ['travel', ...tags],
      // tooltipContent: makeTravelTooltip(baseStaminaCost, baseDistance, duration),
      cost$: combineLatest([
        ri.stats.speed.value$,
        ri.stats.strength.value$,
      ]).pipe(
        map(
          ([spd, str]) =>
            new Map([
              [
                'stamina',
                {
                  value: (step: number) =>
                    step == -1
                      ? new Decimal(1).div(str).times(baseStaminaCost)
                      : new Decimal(1)
                          .div(str)
                          .times(baseStaminaCost)
                          .div(duration),
                  string: () =>
                    `${baseStaminaCost} * ${ri.stats.speed.valueHTML} / ${ri.stats.strength.valueHTML}`,
                },
              ],
            ])
        )
      ),
      effect$: of(effect),
      // effect,
      requiredStats$,
      requiredResources$,
      unlock$,
      visible$,
      conditionString,
    });
    ri.activities[name].setup(overrides);
  };

  overrideTravel(1, 1, {
    name: 'walk',
    displayName: 'Walk',
    description: 'A leisurely stroll',
    unlock$: of(true),
  });

  overrideTravel(5, 3, {
    name: 'jog',
    displayName: 'Jog',
    description: 'A healthy pace. Great for the heart!',
    constitutionMultiplier: 0.01,
    conditionString: `${fancy('walk', 'Walk')} 5 times in a row`,
    unlock$: ri.activities.walk.maxConsecutiveUses$.pipe(map(D.gte(5))),
    visible$: ri.activities.walk.unlock$,
  });

  overrideTravel(10, 5, {
    name: 'run',
    displayName: 'Run',
    description: "Going fast now! Don't hurt yourself!",
    enduranceMultiplier: 0.01,
    requiredStats$: of({constitution: 2}),
    conditionString: 'Jog 5 times in a row',
    unlock$: ri.activities.jog.maxConsecutiveUses$.pipe(map(D.gte(5))),
    visible$: ri.activities.jog.unlock$,
  });

  overrideTravel(50, 20, {
    name: 'cardio',
    displayName: 'Cardio',
    description: '',
    enduranceMultiplier: 0.02,
    constitutionMultiplier: 0.02,
    conditionString: `Run for one hour and jog for one hour`,
    unlock$: all$(
      [ri.activities.run.timeSpent$, ri.activities.jog.timeSpent$],
      map(D.gte(3600))
    ),
    visible$: all$([ri.activities.run.unlock$, ri.activities.jog.unlock$]),
  });

  overrideTravel(25, 12.5, {
    name: 'jump',
    displayName: 'Jump',
    description: 'Jump! Makes you stronger, but the bulk slows you down.',
    duration$: of(2),
    strengthMultiplier: 0.1,
    speedMultiplier: -0.01,
    requiredStats$: of({strength: 10}),
  });

  overrideTravel(100, 25, {
    name: 'dash',
    displayName: 'Dash',
    description:
      'Dash forward! Improves your speed, but you lose a bit of muscle.',
    speedMultiplier: 0.1,
    strengthMultiplier: -0.01,
    requiredStats$: of({speed: 10}),
  });

  function makeTraining(statName: StatName, overrides: Partial<Activity>) {
    const finalOverrides = {};
    assignIfDefined(finalOverrides, {
      name: `train${capitalizeFirstLetter(statName)}`,
      displayName: `Train ${capitalizeFirstLetter(statName)}`,
      tags: ['train', ...(overrides.tags ?? [])],
      duration$: of(5),
      landmarkTags: new Set(['gym']),
      tooltipContent: () =>
        `${overrides.tooltipContent?.()} The final second gives half of the total effect. See it through!`,
      cost$: of(
        new Map([
          [
            'stamina' as Gettable,
            {
              value: (step: number) => (step == -1 ? 10 : 2),
              string: () => '2 per second',
            },
          ],
        ])
      ).pipe(shareReplay(1)),
      effect$: ri.resources.focus.value$.pipe(
        map(
          (focus) =>
            new Map([
              [
                statName,
                {
                  value: (step: number) => {
                    switch (step) {
                      case -1: // Total
                        return focus.div(100).plus(1).times(0.01);
                      case 0: // End
                        return focus.div(100).plus(1).times(0.005);
                      default: // Normal
                        return focus.div(100).plus(1).times(0.00125);
                    }
                  },
                  string: () => `0.1 * (1 + ${focus}/100)`,
                },
              ],
            ])
        )
      ),
      ...overrides,
      unlock$: overrides.unlock$ ?? of(true),
    });
    ri.activities[`train${capitalizeFirstLetter(statName)}`].setup(
      finalOverrides
    );
  }

  makeTraining('strength', {
    description: 'Do you even lift?',
    tooltipContent: () => 'Strength makes exercising cost less Stamina.<br>',
  });

  makeTraining('speed', {
    description: 'Windspriiiiiiints!',
    requiredStats$: of({strength: 5}),
  });

  makeTraining('constitution', {
    description: `I don't have good semantics for this yet.`,
    requiredStats$: of({constitution: 5}),
    tooltipContent: () => 'Constitution improves your Stamina gain.<br>',
  });

  makeTraining('endurance', {
    description: `I don't have good semantics for this yet.`,
    requiredStats$: of({endurance: 5}),
    tooltipContent: () => 'Endurance improves your max Stamina.<br>',
  });

  makeTraining('vitality', {
    description: `I don't have good semantics for this yet.`,
    tooltipContent: () => 'Vitality improves your max Health.<br>',
    requiredActivityLevels$: of({trainStrength: 3}),
  });

  ri.activities.trainStrSpd.setup({
    displayName: 'Crosstrain',
    duration$: of(5),
    description: 'Running with weights. Genius.',
    landmarkTags: new Set(['gym']),
    tooltipContent: () =>
      'The final second gives half of the total effect. Stick it out!',
    tags: new Set(['train']),
    cost$: of(
      new Map([
        [
          'stamina' as Gettable,
          {
            value: (step: number) => (step == -1 ? 10 : 2),
            string: () => '2 per second',
          },
        ],
      ])
    ).pipe(shareReplay(1)),
    effect$: ri.resources.focus.value$.pipe(
      map(
        (focus) =>
          new Map([
            [
              'speed',
              {
                value: (step: number) => {
                  switch (step) {
                    case -1: // Total
                      return focus.div(100).plus(1).times(0.1);
                    case 0: // End
                      return focus.div(100).plus(1).times(0.05);
                    default: // Normal
                      return focus.div(100).plus(1).times(0.0125);
                  }
                },
                string: () => `0.1 * (1 + ${focus}/100)`,
              },
            ],
            [
              'strength',
              {
                value: (step: number) => {
                  switch (step) {
                    case -1: // Total
                      return focus.div(100).plus(1).times(0.1);
                    case 0: // End
                      return focus.div(100).plus(1).times(0.05);
                    default: // Normal
                      return focus.div(100).plus(1).times(0.0125);
                  }
                },
                string: () => `0.1 * (1 + ${focus}/100)`,
              },
            ],
          ])
      )
    ),
    conditionString:
      'Focus over 200,<br>Train Strength and Train Speed 10 times in a row each',
    // condition: () =>
    //   ri.resources.focus.capacity.gte(200) &&
    //   ri.activities.trainStrength.consecutiveUses.gte(10) &&
    //   ri.activities.trainSpeed.consecutiveUses.gte(10),
    // progress: () =>
    //   ri.resources.focus.capacity
    //     .div(200)
    //     .plus(ri.activities.trainStrength.consecutiveUses.div(10))
    //     .plus(ri.activities.trainSpeed.consecutiveUses.div(10))
    //     .div(3),
    unlock$: combineLatest([
      ri.activities.trainStrength.consecutiveUses$,
      ri.activities.trainSpeed.consecutiveUses$,
    ]).pipe(map(([str, spd]) => str.gt(10) && spd.gt(10))),
    visible$: all$([
      ri.activities.trainStrength.unlock$,
      ri.activities.trainSpeed.unlock$,
    ]),
  });

  ri.activities.meditate.setup({
    displayName: 'Meditate',
    description: 'Sit',
    tooltipContent: () => {
      return ``;
    },
    tags: new Set(['meditate']),
    effect$: combineLatest([
      ri.resources.manaDepth.value$,
      ri.stats.wisdom.value$,
    ]).pipe(
      map(
        ([manaDepth, wisdom]) =>
          new Map([
            [
              'mana',
              {
                value: () => manaDepth.times(wisdom).div(100).floor().div(10),
                string: () =>
                  `${wisdom} * ${manaDepth} / 1000, rounded down to steps of 0.1`,
              },
            ],
            ['wisdom', {value: () => 0.001}],
          ])
      )
    ),
    secret: true,
    conditionString: `Monastery Reached, ${fancy(
      'rest',
      'Rest'
    )} 1000 times and then ${fancy('rest', 'Rest')} 30 times in a row`,
    unlock$: combineLatest([
      ri.landmarks.monastery.visited$,
      ri.activities.rest.uses$,
    ]).pipe(
      map(
        ([monasteryVisited, restUses]) => monasteryVisited && restUses.gt(1000)
      )
    ),
    // _progress: () =>
    //   ri.activities.rest.uses
    //     .div(1000)
    //     .min(1)
    //     .plus(ri.activities.rest.consecutiveUses.div(30).min(1))
    //     .div(2),
    visible$: combineLatest([
      ri.landmarks.monastery.visited$,
      ri.activities.rest.uses$,
    ]).pipe(
      map(([monasteryVisited, restUses]) => monasteryVisited && restUses.gt(10))
    ),
  });

  // ri.activities.walkingMeditation.setup( {
  //   displayName: 'Walking Meditation',
  //   description: 'Walk',
  //   tags: new Set(['meditate']),
  //   effect: new Map<
  //     string,
  //     {value: () => DecimalSource; string: () => DecimalSource}
  //   >([
  //     [
  //       'distance',
  //       {
  //         value: () => ri.stats.wisdom.value,
  //         string: () => `${ri.stats.wisdom.valueHTML}`,
  //       },
  //     ],
  //     [
  //       'mana',
  //       {
  //         value: () => ri.stats.wisdom.value,
  //         string: () => `${ri.stats.wisdom.valueHTML}`,
  //       },
  //     ],
  //     [
  //       'manaExp',
  //       {
  //         value: () => ri.stats.wisdom.value,
  //         string: () => `${ri.stats.wisdom.valueHTML}`,
  //       },
  //     ],
  //     ['wisdom', {value: () => 0.001, string: () => '0.001'}],
  //   ]),
  //   secret: true,
  //   conditionString: 'Walked 1000 times and meditated 1000 times',
  //   condition: () =>
  //     ri.getActivityUsesByTag('walk').gte(1000) &&
  //     ri.activities.meditate.uses.gte(1000),
  //   progress: () =>
  //     ri
  //       .getActivityUsesByTag('walk')
  //       .div(1000)
  //       .max(0.5)
  //       .plus(ri.activities.meditate.uses.div(1000).max(0.5))
  //       .div(2),
  //   visibleCondition: () =>
  //     ri.activities.walk.unlocked && ri.activities.meditate.unlocked,
  // });

  // ri.activities.fight.setup( {
  //   displayName: 'Fight',
  //   conditionString: 'Find the arena',
  //   condition: () => false,
  //   progress: () => ri.distance.div(1e4),
  //   visibleCondition: () => false && ri.distance.gte(1000),
  // });

  ri.activities.hunt.setup({
    displayName: 'Track',
    description: 'Hunt down animals.',
    tooltipContent: () =>
      `Costs 1 Stamina each second.<br>At the end of the hunt, you may gain the rewards, but only if you have enough Stamina left!`,
    options: ['rabbit', 'deer'],
    optionsData: {
      rabbit: {
        displayName: 'Hunt rabbits',
        // chance: (step: number) =>
        //   step == 0
        //     ? logisticChance(ri.stats.perception.value, 6, 1.5)
        //     : new Decimal(0), // 5% at 1 PER, 95% at 2 PER
        // chanceString: () =>
        //   `5% at ${fancy('perception', (1).toString())} PER, 95% at ${fancy(
        //     'perception',
        //     (2).toString()
        //   )} PER`,
        cost$: of(
          new Map([
            [
              'stamina' as Gettable,
              {
                value: (step: number) => {
                  switch (step) {
                    case -1:
                      return 5;
                    case 0:
                      return 1;
                    default:
                      return 1;
                  }
                },
                string: () => '1 per second',
              },
            ],
          ])
        ).pipe(shareReplay(1)),
        failCost$: of(
          new Map([['stamina' as Gettable, {value: () => 1}]])
        ).pipe(shareReplay(1)),
        onPerform(_step: number) {
          const slime = ri.findClosestActor('slime');
          if (slime) {
            ri.enqueueTargetPosition(slime.position);
          }
        },
      },
      deer: {
        displayName: 'Hunt deer',
        // chance: (step: number) =>
        //   step == 0
        //     ? logisticChance(ri.stats.perception.value, 6, 1.5)
        //     : new Decimal(0), // 5% at 1 PER, 95% at 2 PER
        // chanceString: () =>
        //   `5% at ${fancy('perception', (1).toString())} PER, 95% at ${fancy(
        //     'perception',
        //     (2).toString()
        //   )} PER`,
        cost$: of(
          new Map([
            [
              'stamina' as Gettable,
              {
                value: (step: number) => {
                  switch (step) {
                    case -1:
                      return 5;
                    case 0:
                      return 1;
                    default:
                      return 1;
                  }
                },
                string: () => '1 per second',
              },
            ],
          ])
        ).pipe(shareReplay(1)),
        failCost$: of(
          new Map([['stamina' as Gettable, {value: () => 1}]])
        ).pipe(shareReplay(1)),
        onPerform(step: number) {
          const deer = ri.findClosestActor('deer');
          if (deer) {
            ri.enqueueTargetPosition(deer.position);
          }
        },
      },
    },
    requiredStats$: of({perception: 10}),
  });

  ri.activities.fishing.setup({
    displayName: 'Go Fishing',
    description: 'Fish for stuff.',
    landmarkTags: new Set(['fishing']),
    requiredLandmarks$: of({fishingPole: true}),
    unlock$: of(true),
    tooltipContent: () => `Relax and catch some perch!`,
    duration$: of(5),
    options: ['use Bait'],
    optionsData: {
      'use Bait': {
        description: 'Use Bait as bait',
        chance$: ri.resources.bait.value$.pipe(
          map((bait) => (_step: number) => logisticChance(bait, 0.0584, 50))
        ),
        chanceString: () =>
          `5% at ${fancy('bait', (0).toString())} Bait, 95% at ${fancy(
            'bait',
            (100).toString()
          )} Bait`,
        cost$: ri.resources.bait.value$.pipe(
          map(
            (bait) =>
              new Map([
                [
                  'bait' as Gettable,
                  {
                    value: () => bait.times(0.1).max(0.1).min(bait),
                    string: () => `10% of your ${bait}`,
                  },
                ],
              ])
          ),
          shareReplay(1)
        ),
        effect$: ri.resources.bait.value$.pipe(
          map(
            (bait) =>
              new Map([
                ['fishing' as Gettable, {value: () => 0.01}],
                ['perch' as Gettable, {weight: () => 50, value: () => 1}],
                [
                  'sunfish' as Gettable,
                  {
                    weight: () => bait.div(10),
                    value: () => 1,
                  },
                ],
              ])
          ),
          shareReplay(1)
        ),
      },
      'use Perch': {
        description: 'Use Perch as bait',
        requiredResources$: of({perch: 1}),
        chance$: ri.resources.perch.value$.pipe(
          map(
            (perch) => (_step: number) =>
              logisticChance(perch, 0.0584, 50).minus(0.05)
          )
        ),
        chanceString: () =>
          `5% at ${fancy('perch', (1).toString())} Perch, 95% at ${fancy(
            'perch',
            (100).toString()
          )} Perch`,
        cost$: ri.resources.perch.value$.pipe(
          map(
            (perch) =>
              new Map([
                [
                  'perch',
                  {
                    value: () => perch.times(0.1).max(0.1).min(perch),
                    string: () => `10% of your ${ri.resources.perch.valueHTML}`,
                  },
                ],
              ])
          )
        ),
        effect$: of(
          new Map([
            ['fishing', {value: () => 0.01}],
            ['sunfish', {weight: () => 50, value: () => 1}],
          ])
        ).pipe(shareReplay(1)),
      },
      'use Sunfish': {
        description: 'Use Sunfish as bait',
        requiredResources: {sunfish: 1},
        chance$: ri.resources.sunfish.value$.pipe(
          map(
            (sunfish) => (_step: number) => logisticChance(sunfish, 0.0584, 50)
          )
        ),
        chanceString: () =>
          `5% at ${fancy('sunfish', (0).toString())}sunfish, 95% at ${fancy(
            'sunfish',
            (100).toString()
          )} Perch`,
        cost$: ri.resources.sunfish.value$.pipe(
          map(
            (sunfish) =>
              new Map([
                [
                  'sunfish',
                  {
                    value: () => sunfish.times(0.1).max(0.1).min(sunfish),
                    string: () =>
                      `10% of your ${ri.resources.sunfish.valueHTML}`,
                  },
                ],
              ])
          )
        ),
        effect$: of(
          new Map([
            ['fishing', {value: () => 0.01}],
            ['sunfish', {value: () => 1}],
          ])
        ).pipe(shareReplay(1)),
      },
    },
  });

  const gatherActivityOptions: Record<string, Partial<ActivityOption>> = {
    Random: {
      description: `Search for something, anything.`,
      chance$: of(() => new Decimal(1)),
      chanceHelpText: () =>
        "Always works, but doesn't always give treasure. Unlock new things to expand the rewards!",
      unlock$: of(true),
      cost$: of(
        new Map([
          [
            'stamina' as Gettable,
            {
              value: () => 1,
              string: () => '1 per second',
            },
          ],
        ])
      ),
      effect$: combineLatest([
        ri.resources.wood.unlock$,
        ri.resources.berry.unlock$,
        ri.stats.strength.value$,
        ri.stats.perception.value$,
      ]).pipe(
        map(
          ([woodUnlocked, berryUnlocked, str, per]) =>
            new Map([
              ['perception', {value: () => 0.001}],
              ['null', {weight: () => 97, value: () => 0}],
              [
                'wood',
                {
                  weight: () => (woodUnlocked ? 1 : 0),
                  value: () => Math.max(1, Math.floor(str.log10())),
                  string: () =>
                    `log_10(${ri.stats.strength.valueHTML}), rounded down, min 1`,
                },
              ],
              [
                'berry',
                {
                  weight: () => (berryUnlocked ? 2 : 0),
                  value: () => Math.max(1, Math.floor(per.log10())),
                  string: () =>
                    `log_10(${ri.stats.perception.valueHTML}), rounded down, min 1`,
                },
              ],
            ])
        )
      ),
    },
    Bookworms: {
      description: `Look for bookworms`,
      landmarkTags: new Set(['books']),
      unlock$: of(true),
      chance$: ri.stats.perception.value$.pipe(
        map(
          (per) => (step: number) =>
            step == 0 ? logisticChance(per, 0.075, 50) : new Decimal(0) // 5% at 10 PER, 95% at 90 PER
        )
      ),
      chanceHelpText: () =>
        `5% at ${fancy('perception', (10).toString())} PER, 95% at ${fancy(
          'perception',
          (90).toString()
        )} PER`,
      cost$: of(new Map([['stamina' as Gettable, {value: () => 1}]])),
      effect$: of(
        new Map([
          [
            'bookworms',
            {
              value: () => 1,
            },
          ],
          ['nothing', {weight: () => 99, value: () => 0}],
          ['magicBookworms', {weight: () => 1, value: () => 1}],
        ])
      ),
    },
    Wood: {
      description: `Chop down trees`,
      landmarkTags: new Set(['forest']),
      unlock$: of(true),
      chance$: ri.stats.perception.value$.pipe(
        map(
          (per) => (step: number) =>
            step == 0 ? logisticChance(per, 3, 1) : new Decimal(0)
        )
      ), // 50% at 1 PER, 95% at 2 PER
      chanceHelpText: () =>
        `50% at ${fancy('perception', (1).toString())} PER, 95% at ${fancy(
          'perception',
          (2).toString()
        )} PER`,
      cost$: of(
        new Map([
          [
            'stamina',
            {value: () => 5, string: () => '5 if successful, 1 if not'},
          ],
        ])
      ),
      failCost: new Map([['stamina', {value: () => 1}]]),
      effect$: ri.stats.strength.value$.pipe(
        map(
          (strengthValue) =>
            new Map([
              [
                'wood',
                {
                  value: () => Math.max(1, Math.floor(strengthValue.log10())),
                  string: () =>
                    `log_10(${ri.stats.strength.valueHTML}), rounded down, min 1`,
                },
              ],
            ])
        )
      ),
    },
    Berries: {
      description: 'Search for berries',
      landmarkTags: new Set(['plants']),
      chance: (step: number) =>
        step == 0
          ? logisticChance(ri.stats.perception.value, 1, 3.1)
          : new Decimal(0), // 25% at 2 PER, 95% at 6 PER
      chanceHelpText: () =>
        `25% at ${fancy('perception', (2).toString())} PER, 95% at ${fancy(
          'perception',
          (6).toString()
        )} PER`,
      cost$: of(new Map([['stamina', {value: () => 1}]])),
      effect$: ri.stats.perception.value$.pipe(
        map(
          (perceptionValue) =>
            new Map([
              [
                'berry',
                {
                  value: () =>
                    new Decimal(perceptionValue.floor().log(2)).clamp(1, 100),
                  string: () =>
                    `log_2(${ri.stats.perception.valueHTML}), rounded down, min 1, max 100`,
                },
              ],
              [
                'perception',
                {
                  value: () =>
                    new Decimal(perceptionValue.floor().log(2))
                      .clamp(1, 100)
                      .times(0.001),
                  string: () => `0.001 per Berry`,
                },
              ],
            ])
        )
      ),
      unlock$: ri.stats.perception.value$.pipe(map((per) => per.gt(2))),
    },
    Bait: {
      name: 'bait',
      description: 'Search for bait',
      landmarkTags: new Set(['fishing']),
      chance: (_step: number) => 1,
      cost$: of(new Map([['stamina', {value: () => 1}]])),
      effect$: ri.stats.perception.value$.pipe(
        map(
          (perceptionValue) =>
            new Map([
              [
                'bait',
                {
                  value: () => perceptionValue.floor().clamp(1, 100),
                  string: () =>
                    `${ri.stats.perception.valueHTML}, rounded down, min 1, max 100`,
                },
              ],
              [
                'perception',
                {
                  value: () => 0.001,
                  string: () => `0.001`,
                },
              ],
            ])
        )
      ),
      unlock$: ri.activities.fishing.unlock$,
    },
    'Iron Ore': {
      name: 'ironOre',
      description: 'Mine Iron Ore',
      landmarkTags: new Set(['ore']),
      unlock$: of(true),
      chance: (step: number) =>
        step == 0
          ? logisticChance(ri.stats.strength.value, 1.2, 100)
          : new Decimal(0), // 10% at 100 STR, 90% at 500 STR
      chanceHelpText: () =>
        `10% at ${fancy('strength', (100).toString())} STR, 90% at ${fancy(
          'strength',
          (500).toString()
        )} STR`,
      cost$: of(new Map([['stamina', {value: () => 1}]])),
      effect$: ri.stats.strength.value$.pipe(
        map(
          (strengthValue) =>
            new Map([
              [
                'ironOre',
                {
                  value: () => strengthValue.floor().clamp(1, 100),
                  string: () =>
                    `${ri.stats.perception.valueHTML}, rounded down, min 1, max 100`,
                },
              ],
              [
                'strength',
                {
                  value: () => strengthValue.floor().clamp(1, 100).div(1000),
                  string: () => `0.001 per ironOre`,
                },
              ],
            ])
        )
      ),
    },
  };
  ri.activities.gather.setup({
    displayName: 'Gather',
    description: 'Gather resources.',
    tooltipContent: () => `Costs Stamina each second. Randomly find rewards.`,
    options: [],
    optionsData: gatherActivityOptions,
    secret: true,
    requiredStats$: of({perception: 2}),
  });

  ri.activities.lightFire.setup({
    displayName: 'Stoke Fire',
    description: 'Add fuel to the fire!',
    tooltipContent: () => `Uses Wood to make Fire`,
    options: ['Wood'],
    optionsData: {
      Wood: {
        displayName: 'Burn wood',
        chance: () => 1,
        cost$: of(new Map([['wood', {value: () => 1}]])),
        effect$: of(new Map([['fire', {value: () => 5}]])),
        // failEffect: new Map([
        //   ['burntMeat', {value: (step) => (step == 0 ? 1 : 0)}],
        // ]),
      },
    },
    secret: true,
    conditionString: 'Find something to burn',
    unlock$: ri.resources.wood.maxValue$.pipe(map(D.gt(0))),
    visible$: ri.resources.wood.maxValue$.pipe(map(D.gt(0))),
  });

  ri.activities.cook.setup({
    displayName: 'Cook',
    description: 'Make food.',
    tooltipContent: () =>
      `Costs ingredients and maybe fuel at the start. At the end, you gain the cooked food.`,
    duration$: of(5),
    options: ['meat'],
    optionsData: {
      meat: {
        description: 'Cook meat',
        chance: (step: number) => (step == 0 ? 1 : 0),
        cost$: of(
          new Map([
            [
              'fire',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
            [
              'meat',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
          ])
        ),
        effect$: of(new Map([['cookedMeat', {value: () => 1}]])),
        // failEffect: new Map([
        //   ['burntMeat', {value: (step) => (step == 0 ? 1 : 0)}],
        // ]),
      },
      'Slime Milk': {
        description: 'Concentrate Gel into Slime Milk',
        chance: (step: number) => (step == 0 ? 1 : 0),
        cost$: of(
          new Map([
            // [
            //   'fire',
            //   {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            // ],
            [
              'gel',
              {value: (step: number) => ([-1, 1].includes(step) ? 3 : 0)},
            ],
          ])
        ),
        effect$: of(
          new Map([
            [
              'slimeMilk',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
          ])
        ),
        unlock$: ri.resources.gel.unlock$,
      },

      // 'flash-fire': {
      //   description: 'Not quite a buffalo, not quite 40 seconds',
      //   chance: (step: number) => (step == 0 ? 1 : 0),
      //   cost: new Map([
      //     ['fire', {value: (step: number) => ([-1].includes(step) ? 50 : 5)}],
      //     [
      //       'meat',
      //       {value: (step: number) => ([-1, 1].includes(step) ? 100 : 0)},
      //     ],
      //   ]),
      //   effect: new Map([['cookedMeat', {value: () => 100}]]),
      //   duration: 10,
      //   // failEffect: new Map([
      //   //   ['burntMeat', {value: (step) => (step == 0 ? 1 : 0)}],
      //   // ]),
      // },
      pemmican: {
        description: 'Make pemmican',
        chance: (step: number) => (step == 0 ? 1 : 0),
        cost$: of(
          new Map([
            [
              'meat',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
            [
              'berry',
              {value: (step: number) => ([-1, 1].includes(step) ? 5 : 0)},
            ],
          ])
        ),
        effect$: of(new Map([['pemmican', {value: () => 1}]])),
        unlock$: combineLatest([
          ri.resources.berry.unlock$,
          ri.resources.meat.unlock$,
        ]).pipe(map(all(identity))),
      },
      pemmicanFire: {
        description: 'Make pemmican with fire',
        chance: (step: number) => (step == 0 ? 1 : 0),
        cost$: of(
          new Map([
            [
              'fire',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
            [
              'meat',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
            [
              'berry',
              {value: (step: number) => ([-1, 1].includes(step) ? 5 : 0)},
            ],
          ])
        ),
        effect$: of(new Map([['pemmican', {value: () => 1}]])),
        unlock$: combineLatest([
          ri.resources.berry.unlock$,
          ri.resources.meat.unlock$,
        ]).pipe(map(all(identity))),
      },
    },
    conditionString: 'Find a way to cook your food.',
    unlock$: any$([
      ri.resources.fire.unlock$,
      ri.resources.meat.unlock$,
      ri.resources.gel.unlock$,
    ]),
    visible$: ri.resources.meat.unlock$,
  });

  ri.activities.eat.setup({
    displayName: 'Consume',
    description:
      'Eat some food, drink a potion, break a magic crystal. Take the thing and put it inside you!',
    tooltipContent: () => `If you're too full, you won't eat anything.`,
    optionsByTag: ['food'],
    optionsData: {
      Gel: {
        description:
          'Eat Gel. Reduces the duration of a random status effect, good or bad.',
        chance: () => 1,
        cost$: of(
          new Map([
            ['nutrition', {value: () => -5}],
            ['gel', {value: () => 1}],
          ])
        ),
        onPerform() {
          const index = Math.floor(ri.statusEffects.length * Math.random());
          ri.statusEffects[index].timeLeft =
            ri.statusEffects[index].timeLeft.minus(10);
        },
        unlock$: ri.resources.gel.unlock$,
        hideEntry: true,
      },
      'Slime Milk': {
        description:
          'Drink Slime Milk. Clears away a random status effect, good or bad.',
        chance: () => 1,
        cost$: of(
          new Map([
            ['nutrition', {value: () => -5}],
            ['slimeMilk', {value: () => 1}],
          ])
        ),
        onPerform() {
          ri.statusEffects.splice(
            Math.floor(ri.statusEffects.length * Math.random()),
            1
          );
        },
        unlock$: ri.resources.slimeMilk.unlock$,
        hideEntry: true,
      },
      'Health Potion': {
        description:
          'Drink a health potion. Clears away a random status effect, good or bad.',
        chance: () => 1,
        cost$: of(
          new Map([
            ['nutrition', {value: () => -5}],
            ['healthPotion', {value: () => 1}],
          ])
        ),
        effect$: of(new Map([['health', {value: () => 10}]])),
        unlock$: ri.resources.healthPotion.unlock$,
        hideEntry: true,
      },
      Meat: {
        description: 'Eat raw Meat. Juicy!',
        chance: () => 1,
        cost$: of(
          new Map([
            [
              'fire',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
            ['meat', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ])
        ),
        effect$: of(new Map([['constitution', {value: () => 0.01}]])),
        unlock$: ri.resources.meat.unlock$,
        hideEntry: true,
      },
      'Cooked Meat': {
        description: 'Eat a nice Cooked Meat. Tasty!',
        chance: () => 1,
        cost$: of(
          new Map([
            ['cookedMeat', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ])
        ),
        effect$: of(new Map([['constitution', {value: () => 0.05}]])),
        unlock$: ri.resources.cookedMeat.unlock$,
        hideEntry: true,
      },
      Berries: {
        description: 'Eat mystery Berries. Yum!',
        chance: () => 1,
        cost$: of(
          new Map([
            ['berry', {value: () => 1}],
            ['nutrition', {value: () => -0.1}],
          ])
        ),
        effect$: of(
          new Map([
            ['stamina', {value: () => 1}],
            ['manaExp', {value: () => 1}],
          ])
        ),
        unlock$: ri.resources.berry.unlock$,
        hideEntry: true,
      },
      Pemmican: {
        description: 'Eat some Pemmican.',
        chance: () => 1,
        cost$: of(
          new Map([
            ['pemmican', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ])
        ),
        effect$: of(
          new Map([
            ['constitution', {value: () => 0.1}],
            ['mana', {value: () => 10}],
          ])
        ),
        unlock$: ri.resources.pemmican.unlock$,
        hideEntry: true,
      },
      Perch: {
        description: 'Eat some Perch.',
        chance: () => 1,
        cost$: of(
          new Map([
            ['perch', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ])
        ),
        effect$: of(
          new Map([
            [
              'stamina',
              {
                value: () => ri.resources.stamina.level,
                string: () => `${fancy('stamina', 'Stamina')} level`,
              },
            ],
          ])
        ),
        unlock$: ri.resources.perch.unlock$,
        hideEntry: true,
      },
      Sunfish: {
        description: 'Eat a sunfish :(',
        chance: () => 1,
        cost$: of(
          new Map([
            ['sunfish', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ])
        ),
        effect$: of(new Map([['light', {value: () => 5}]])),
        unlock$: ri.resources.sunfish.unlock$,
        hideEntry: true,
      },
      Bookworms: {
        description: 'Eat a common bookworm. Surprisingly crunchy!',
        chance: () => 1,
        cost$: of(
          new Map([
            ['bookworms', {value: () => 1}],
            ['nutrition', {value: () => -1}],
          ])
        ),
        effect$: of(new Map([['intelligence', {value: () => 0.01}]])),
        unlock$: ri.resources.bookworms.unlock$,
        hideEntry: true,
      },
      'Arcane Bookworms': {
        description: 'Eat an arcane bookworm. Tingly!',
        chance: () => 1,
        cost$: of(
          new Map([
            ['magicBookworms', {value: () => 1}],
            ['nutrition', {value: () => -1}],
          ])
        ),
        effect$: of(
          new Map([
            ['intelligence', {value: () => 0.01}],
            ['magic', {value: () => 0.01}],
          ])
        ),
        unlock$: ri.resources.magicBookworms.unlock$,
        hideEntry: true,
      },
    },
    // conditionString: 'Find some food',
    // condition: () =>
    //   Object.values(ri.resources).some((r) => r.tags.has('food') && r.unlocked),
    // visibleCondition: () =>
    //   Object.values(ri.resources).some((r) => r.tags.has('food') && r.unlocked),
  });

  const makeBook = (obj: {
    name: string;
    description?: string;
    initialChance?: number;
    maxUses: number;
    cost$?: DynamicCost;
    effect$?: DynamicCost;
    requiredStats?: Record<string, DecimalSource>;
    requiredLandmarks?: Record<string, boolean>;
    requiredResources?: Record<string, DecimalSource>;
    visibleStats?: Record<string, DecimalSource>;
  }) => {
    const {
      name,
      description = 'A book',
      initialChance = 1,
      maxUses = 1,
      cost$ = of(new Map()),
      effect$ = of(new Map()),
      requiredStats$ = of({}),
      // requiredLandmarks = {},
      // requiredResources = {},
      visibleStats$ = of({}),
    } = obj;
    return {
      name,
      displayName: `Read ${name}`,
      description,
      chance$: of(() =>
        new Decimal(initialChance)
          .minus(
            ri.activities.read.optionsData[name].successfulUses$.value
              .times(initialChance)
              .div(maxUses)
          )
          .max(0)
      ),
      chanceLabel: () =>
        `${new Decimal(maxUses).minus(
          ri.activities.read.optionsData[name].successfulUses$.value
        )} / ${maxUses} Insights Remaining<br>Chance for Insight`,
      lockCondition: () =>
        ri.activities.read.optionsData[name].successfulUses$.value.gte(maxUses),
      cost$,
      effect$,
      requiredStats$,
      visibleStats$,
      unlockAtTags: new Set(['books']),
    };
  };

  ri.activities.read.setup({
    displayName: 'Read',
    description: "Get some learnin'.",
    tooltipContent: () => `Read books and gain mental Stats`,
    options: ['Studying for Dummies'],
    optionsData: Object.fromEntries(
      [
        makeBook({
          name: 'Studying for Dummies',
          maxUses: 200,
          effect$: of(new Map([['intelligence', {value: () => 0.01}]])),
        }),
        makeBook({
          name: 'The Book of General Knowledge',
          initialChance: 0.9,
          maxUses: 500,
          effect$: of(new Map([['intelligence', {value: () => 0.02}]])),
          requiredStats$: of({intelligence: 4}),
        }),
        makeBook({
          name: 'The Book of General Ignorance',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(new Map([['intelligence', {value: () => 0.04}]])),
          requiredStats$: of({intelligence: 16}),
        }),
        makeBook({
          name: 'Magic: A Primer',
          initialChance: 0.9,
          maxUses: 500,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['magic', {value: () => 0.01}],
              ['null', {weight: () => 48, value: () => 0}],
              ['fireMagic', {weight: () => 1, value: () => 0.1}],
              ['lightMagic', {weight: () => 1, value: () => 0.1}],
              // ['spaceMagic', {weight: () => 1, value: () => 1}],
              // ['timeMagic', {weight: () => 1, value: () => 1}],
              // ['crystalMagic', {weight: () => 1, value: () => 1}],
            ])
          ),
          requiredStats$: of({intelligence: 2}),
        }),
        makeBook({
          name: 'Intermediate Magic: Fundamental Truths',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['magic', {value: () => 0.01}],
              ['null', {weight: () => 95, value: () => 0}],
              ['lightMagic', {weight: () => 1, value: () => 0.1}],
              ['fireMagic', {weight: () => 1, value: () => 0.1}],
              ['spaceMagic', {weight: () => 1, value: () => 0.1}],
              ['timeMagic', {weight: () => 1, value: () => 0.1}],
            ])
          ),
          requiredStats$: of({intelligence: 5}),
          visibleStats$: of({intelligence: 2}),
        }),
        makeBook({
          name: "The Old Wizard's Almanac",
          maxUses: 1000,
          effect$: of(new Map([['magic', {value: () => 0.01}]])),
          requiredStats$: of({magic: 50}),
        }),
        makeBook({
          name: 'A Light in the Dark',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['null', {weight: () => 10, value: () => 0}],
              ['lightMagic', {weight: () => 1, value: () => 0.1}],
            ])
          ),
          requiredStats$: of({intelligence: 5, lightMagic: 1}),
        }),
        makeBook({
          name: 'How to Conjure Fire',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['fire', {weight: () => 10, value: () => 10}],
              ['fireMagic', {weight: () => 1, value: () => 0.1}],
            ])
          ),
          requiredStats$: of({intelligence: 5, fireMagic: 1}),
        }),
        makeBook({
          name: 'Space: The Final Frontier',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['null', {weight: () => 10, value: () => 0}],
              ['lightMagic', {weight: () => 1, value: () => 0.1}],
            ])
          ),
          requiredStats: {intelligence: 5, spaceMagic: 1},
        }),
        makeBook({
          name: 'Tesseracts and Time Travel',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['null', {weight: () => 10, value: () => 0}],
              ['timeMagic', {weight: () => 1, value: () => 0.1}],
            ])
          ),
          requiredStats: {intelligence: 5, timeMagic: 1},
        }),
        makeBook({
          name: 'Crystals and You',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['null', {weight: () => 10, value: () => 0}],
              ['crystalMagic', {weight: () => 1, value: () => 0.1}],
            ])
          ),
          requiredStats: {intelligence: 5, crystalMagic: 1},
        }),
        makeBook({
          name: 'Ichthyology 101',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['null', {weight: () => 9, value: () => 0}],
              ['fishing', {weight: () => 1, value: () => 0.1}],
            ])
          ),
          requiredStats: {intelligence: 5, fishing: 1},
        }),
        makeBook({
          name: 'Ichthyology 102',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.01}],
              ['null', {weight: () => 8, value: () => 0}],
              ['fishing', {weight: () => 2, value: () => 0.1}],
            ])
          ),
          requiredStats: {intelligence: 10, fishing: 5},
          visibleStats: {intelligence: 5, fishing: 1},
        }),
        makeBook({
          name: 'Ichthyology 201',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.02}],
              ['null', {weight: () => 8, value: () => 0}],
              ['fishing', {weight: () => 2, value: () => 0.1}],
            ])
          ),
          requiredStats: {intelligence: 50, fishing: 25},
          visibleStats: {intelligence: 10, fishing: 5},
        }),
        makeBook({
          name: 'Ichthyology 202',
          initialChance: 0.5,
          maxUses: 1000,
          effect$: of(
            new Map([
              ['intelligence', {value: () => 0.02}],
              ['null', {weight: () => 6, value: () => 0}],
              ['fishing', {weight: () => 4, value: () => 0.1}],
            ])
          ),
          requiredStats: {intelligence: 100, fishing: 50},
          visibleStats: {intelligence: 50, fishing: 25},
        }),
      ].map((o) => [o.name, o])
    ),
    secret: true,
    requiredLandmarks$: of({library: true}),
  });
}
